import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Replacement for `window.confirm()` — a CadencEZ-styled confirm dialog.
 *
 * Usage:
 *
 *   const [pending, setPending] = useState<string | null>(null);
 *   return (
 *     <>
 *       <button onClick={() => setPending(item.id)}>Delete</button>
 *       <ConfirmModal
 *         open={pending !== null}
 *         title={t('workbook.delete_confirm')}
 *         confirmLabel={t('common.delete')}
 *         destructive
 *         onConfirm={() => { del.mutate(pending!); setPending(null); }}
 *         onCancel={() => setPending(null)}
 *       />
 *     </>
 *   );
 *
 * Why this and not `window.confirm`:
 *   - Native confirm blocks the browser thread and breaks design coherence
 *   - The CadencEZ editorial design system (hairlines, slate+emerald, no shadow
 *     stacks) cannot survive a native chrome dialog rendered alongside it
 *   - The user has flagged native-dialog usage as a recurring complaint
 */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive,
  onConfirm,
  onCancel,
  busy,
}: {
  open: boolean;
  title: string;
  body?: string;
  /** Override the default "Confirm" label. */
  confirmLabel?: string;
  /** Override the default "Cancel" label. */
  cancelLabel?: string;
  /** Use rose styling for the confirm button (deletes, irreversible actions). */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** Show a loading state on the confirm button while the action runs. */
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Close on Escape, and properly trap focus inside the dialog (a11y modal
  // contract): on open we remember the trigger, move focus to the confirm
  // button, keep Tab/Shift+Tab cycling within the dialog, and restore focus to
  // the trigger when the dialog closes.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusable = cardRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      previouslyFocused?.focus?.();
    };
  }, [open, onCancel]);

  if (!open) return null;

  const confirmCls = destructive
    ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-foreground hover:bg-foreground/90 text-background';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Backdrop — click to cancel */}
      <button
        type="button"
        aria-label={cancelLabel ?? t('common.cancel')}
        onClick={onCancel}
        className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
      />
      {/* Dialog card */}
      <div ref={cardRef} className="relative w-full max-w-md rounded-md border border-border bg-card shadow-xl">
        <div className="px-6 pt-5 pb-4">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {body && <p className="mt-2 text-sm text-muted-foreground">{body}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 pb-5">
          <button
            onClick={onCancel}
            disabled={busy}
            className="h-9 px-3.5 rounded-md border border-border text-[12.5px] font-semibold text-foreground hover:bg-foreground/[0.04] disabled:opacity-60"
          >
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            className={`h-9 px-3.5 rounded-md text-[12.5px] font-semibold disabled:opacity-60 ${confirmCls}`}
          >
            {busy ? t('settings.saving') : (confirmLabel ?? t('common.save'))}
          </button>
        </div>
      </div>
    </div>
  );
}
